import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  NotificationSenderAddressRequest,
  NotificationSenderAddressResponse,
  getAllSenderAddresses,
  createSenderAddress,
  updateSenderAddress,
  deleteSenderAddress,
  toggleSenderAddressActivation,
  getSenderAddressDetails,
  GetSenderAddressDetailsResponse,
  GetAllSenderAddressesResponse,
  UpdateNotificationSenderAddressRequest,
} from '@app/api/notification.api'
import {
  AdditionalNotificationRecipientData,
  AdditionalNotificationRecipientResponse,
  UpdateAdditionalNotificationRecipientRequest,
  createAdditionalNotificationRecipient,
  deleteAdditionalNotificationRecipient,
  getAllAdditionalNotificationRecipients,
  updateAdditionalNotificationRecipient,
} from '@app/api/additional-notification-recipient.api'
import {
  NotificationTemplateRequest,
  NotificationTemplateResponse,
  createNotificationTemplate,
  deleteNotificationTemplate,
  getAllNotificationTemplates,
  updateNotificationTemplate,
} from '@app/api/notification-template.api'
import {
  NotificationRequest,
  NotificationResponse,
  UpdateNotificationRequest,
  createNotification,
  deleteNotification,
  getAllNotifications,
  toggleNotificationActive,
  updateNotification,
} from '@app/api/notification.api'

import { getErrorMessage } from '@/utils/utils'
import { Status } from '@/types/general-types'

type NotificationState = {
  senderAddresses: Record<string, NotificationSenderAddressResponse | Partial<NotificationSenderAddressResponse>>
  senderAddressesStatus: Status
  additionalNotificationRecipient: Record<string, AdditionalNotificationRecipientData>
  additionalNotificationRecipientStatus: Status
  notificationTemplates: Record<string, NotificationTemplateResponse>
  notificationTemplatesStatus: Status
  notifications: Record<string, NotificationResponse>
  notificationsStatus: Status
}

const initialState: NotificationState = {
  senderAddresses: {},
  senderAddressesStatus: 'idle',
  additionalNotificationRecipientStatus: 'idle',
  additionalNotificationRecipient: {},
  notificationTemplates: {},
  notificationTemplatesStatus: 'idle',
  notifications: {},
  notificationsStatus: 'idle',
}

export const getAllSenderAddressesData = createAsyncThunk<GetAllSenderAddressesResponse[]>(
  'notification/getAllSenderAddressesData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllSenderAddresses()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getSenderAddressDetailsData = createAsyncThunk<GetSenderAddressDetailsResponse, string>(
  'notification/getSenderAddressDetailsData',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getSenderAddressDetails(id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createSenderAddressData = createAsyncThunk<string, NotificationSenderAddressRequest>(
  'notification/createSenderAddressData',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createSenderAddress(payload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateSenderAddressData = createAsyncThunk<void, UpdateNotificationSenderAddressRequest>(
  'notification/updateSenderAddressData',
  async (payload, { rejectWithValue }) => {
    try {
      await updateSenderAddress(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteSenderAddressData = createAsyncThunk<void, string>(
  'notification/deleteSenderAddressData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteSenderAddress(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllAdditionalNotificationRecipientsData = createAsyncThunk<AdditionalNotificationRecipientResponse[]>(
  'notification/getAllAdditionalNotificationRecipientsData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllAdditionalNotificationRecipients()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateAdditionalNotificationRecipientData = createAsyncThunk<
  void,
  UpdateAdditionalNotificationRecipientRequest
>('notification/updateAdditionalNotificationRecipientData', async (payload, { rejectWithValue }) => {
  try {
    await updateAdditionalNotificationRecipient(payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteAdditionalNotificationRecipientData = createAsyncThunk<void, string>(
  'notification/deleteAdditionalNotificationRecipientData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteAdditionalNotificationRecipient(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createAdditionalNotificationRecipientData = createAsyncThunk<string, AdditionalNotificationRecipientData>(
  'notification/createAdditionalNotificationRecipientData',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createAdditionalNotificationRecipient(payload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllNotificationsData = createAsyncThunk<NotificationResponse[]>(
  'notification/getAllNotificationsData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllNotifications()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createNotificationData = createAsyncThunk<NotificationResponse, NotificationRequest>(
  'notification/createNotificationData',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createNotification(payload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateNotificationData = createAsyncThunk<
  NotificationResponse,
  { id: string; data: UpdateNotificationRequest }
>('notification/updateNotificationData', async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await updateNotification(id, data)

    return res
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteNotificationData = createAsyncThunk<void, string>(
  'notification/deleteNotificationData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteNotification(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const toggleNotificationActiveData = createAsyncThunk<
  NotificationResponse,
  { id: string; isActive: boolean; performedById?: string }
>('notification/toggleNotificationActiveData', async ({ id, isActive, performedById }, { rejectWithValue }) => {
  try {
    const res = await toggleNotificationActive(id, { isActive, performedById })

    return res
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const getAllNotificationTemplatesData = createAsyncThunk<NotificationTemplateResponse[]>(
  'notification/getAllNotificationTemplatesData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllNotificationTemplates()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createNotificationTemplateData = createAsyncThunk<string, NotificationTemplateRequest>(
  'notification/createNotificationTemplateData',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createNotificationTemplate(payload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateNotificationTemplateData = createAsyncThunk<
  void,
  { id: string; data: Partial<NotificationTemplateRequest> }
>('notification/updateNotificationTemplateData', async ({ id, data }, { rejectWithValue }) => {
  try {
    await updateNotificationTemplate(id, data)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteNotificationTemplateData = createAsyncThunk<void, string>(
  'notification/deleteNotificationTemplateData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteNotificationTemplate(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const toggleSenderAddressActivationData = createAsyncThunk<void, { id: string; isActive: boolean }>(
  'notification/toggleSenderAddressActivationData',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      await toggleSenderAddressActivation(id, { isActive })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateAdditionalNotificationRecipientData.pending, (state) => {
        state.additionalNotificationRecipientStatus = 'loading'
      })
      .addCase(getAllNotificationsData.pending, (state) => {
        state.notificationsStatus = 'loading'
      })
      .addCase(createNotificationData.pending, (state) => {
        state.notificationsStatus = 'loading'
      })
      .addCase(updateNotificationData.pending, (state) => {
        state.notificationsStatus = 'loading'
      })
      .addCase(deleteNotificationData.pending, (state) => {
        state.notificationsStatus = 'loading'
      })
      .addCase(toggleNotificationActiveData.pending, (state) => {
        state.notificationsStatus = 'loading'
      })
      .addCase(getAllNotificationTemplatesData.pending, (state) => {
        state.notificationTemplatesStatus = 'loading'
      })
      .addCase(createNotificationTemplateData.pending, (state) => {
        state.notificationTemplatesStatus = 'loading'
      })
      .addCase(updateNotificationTemplateData.pending, (state) => {
        state.notificationTemplatesStatus = 'loading'
      })
      .addCase(deleteNotificationTemplateData.pending, (state) => {
        state.notificationTemplatesStatus = 'loading'
      })
      .addCase(getAllSenderAddressesData.pending, (state) => {
        state.senderAddressesStatus = 'loading'
      })
      .addCase(updateSenderAddressData.pending, (state) => {
        state.senderAddressesStatus = 'loading'
      })
      .addCase(deleteSenderAddressData.pending, (state) => {
        state.senderAddressesStatus = 'loading'
      })
      .addCase(toggleSenderAddressActivationData.pending, (state) => {
        state.senderAddressesStatus = 'loading'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.pending, (state) => {
        state.additionalNotificationRecipientStatus = 'loading'
      })
      .addCase(deleteAdditionalNotificationRecipientData.pending, (state) => {
        state.additionalNotificationRecipientStatus = 'loading'
      })
      .addCase(getAllSenderAddressesData.rejected, (state) => {
        state.senderAddressesStatus = 'failed'
      })
      .addCase(updateSenderAddressData.rejected, (state) => {
        state.senderAddressesStatus = 'failed'
      })
      .addCase(deleteSenderAddressData.rejected, (state) => {
        state.senderAddressesStatus = 'failed'
      })
      .addCase(toggleSenderAddressActivationData.rejected, (state) => {
        state.senderAddressesStatus = 'failed'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.rejected, (state) => {
        state.additionalNotificationRecipientStatus = 'failed'
      })
      .addCase(updateAdditionalNotificationRecipientData.rejected, (state) => {
        state.additionalNotificationRecipientStatus = 'failed'
      })
      .addCase(deleteAdditionalNotificationRecipientData.rejected, (state) => {
        state.additionalNotificationRecipientStatus = 'failed'
      })
      .addCase(createAdditionalNotificationRecipientData.rejected, (state) => {
        state.additionalNotificationRecipientStatus = 'failed'
      })
      .addCase(getAllNotificationsData.rejected, (state) => {
        state.notificationsStatus = 'failed'
      })
      .addCase(createNotificationData.rejected, (state) => {
        state.notificationsStatus = 'failed'
      })
      .addCase(updateNotificationData.rejected, (state) => {
        state.notificationsStatus = 'failed'
      })
      .addCase(deleteNotificationData.rejected, (state) => {
        state.notificationsStatus = 'failed'
      })
      .addCase(toggleNotificationActiveData.rejected, (state) => {
        state.notificationsStatus = 'failed'
      })
      .addCase(getAllNotificationTemplatesData.rejected, (state) => {
        state.notificationTemplatesStatus = 'failed'
      })
      .addCase(createNotificationTemplateData.rejected, (state) => {
        state.notificationTemplatesStatus = 'failed'
      })
      .addCase(updateNotificationTemplateData.rejected, (state) => {
        state.notificationTemplatesStatus = 'failed'
      })
      .addCase(deleteNotificationTemplateData.rejected, (state) => {
        state.notificationTemplatesStatus = 'failed'
      })
      .addCase(getAllSenderAddressesData.fulfilled, (state, action) => {
        action.payload.forEach((senderAddress) => {
          const id = senderAddress.id
          const existing = state.senderAddresses[id]

          state.senderAddresses[id] = {
            ...existing,
            ...senderAddress,
          }
        })

        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(getSenderAddressDetailsData.fulfilled, (state, action) => {
        const resp = action.payload
        const id = action.meta.arg
        const existing = state.senderAddresses[id]

        state.senderAddresses[id] = {
          ...existing,
          ...resp,
        }
        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(createSenderAddressData.fulfilled, (state, action) => {
        const id = action.payload
        const { password, ...restData } = action.meta.arg

        state.senderAddresses[id] = {
          id,
          ...restData,
        }

        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(updateSenderAddressData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        const { currentPassword, newPassword, ...restData } = data
        const existing = state.senderAddresses[id]

        state.senderAddresses[id] = {
          ...existing,
          ...restData,
        }

        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(deleteSenderAddressData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.senderAddresses[id]

        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(toggleSenderAddressActivationData.fulfilled, (state, action) => {
        const { id, isActive } = action.meta.arg
        const existing = state.senderAddresses[id]

        state.senderAddresses[id] = {
          ...existing,
          isActive,
        }

        if (isActive) {
          Object.keys(state.senderAddresses).forEach((key) => {
            if (key !== id) {
              state.senderAddresses[key] = {
                ...state.senderAddresses[key],
                isActive: false,
              }
            }
          })
        }
        state.senderAddressesStatus = 'succeeded'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.fulfilled, (state, action) => {
        action.payload.forEach((recipient) => {
          const id = recipient.id
          const existing = state.additionalNotificationRecipient[id]

          state.additionalNotificationRecipient[id] = {
            ...existing,
            ...recipient,
          }
        })

        state.additionalNotificationRecipientStatus = 'succeeded'
      })
      .addCase(updateAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        const existing = state.additionalNotificationRecipient[id]

        state.additionalNotificationRecipient[id] = {
          ...existing,
          ...data,
        }

        state.additionalNotificationRecipientStatus = 'succeeded'
      })
      .addCase(deleteAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.additionalNotificationRecipient[id]

        state.additionalNotificationRecipientStatus = 'succeeded'
      })
      .addCase(createAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        const data = action.meta.arg
        const id = action.payload
        const newAdditionalNotificationRecipient = {
          ...data,
          id,
        }

        state.additionalNotificationRecipient = {
          [id]: newAdditionalNotificationRecipient,
          ...state.additionalNotificationRecipient,
        }

        state.additionalNotificationRecipientStatus = 'succeeded'
      })
      .addCase(getAllNotificationsData.fulfilled, (state, action) => {
        action.payload.forEach((notification) => {
          state.notifications[notification.id] = {
            ...(state.notifications[notification.id] || {}),
            ...notification,
          }
        })

        state.notificationsStatus = 'succeeded'
      })
      .addCase(createNotificationData.fulfilled, (state, action) => {
        const notification = action.payload

        state.notifications = {
          [notification.id]: notification,
          ...state.notifications,
        }

        state.notificationsStatus = 'succeeded'
      })
      .addCase(updateNotificationData.fulfilled, (state, action) => {
        const updated = action.payload
        const id = updated.id
        const existing = state.notifications[id] || {}

        state.notifications[id] = {
          ...existing,
          ...updated,
        }

        state.notificationsStatus = 'succeeded'
      })
      .addCase(deleteNotificationData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.notifications[id]

        state.notificationsStatus = 'succeeded'
      })
      .addCase(toggleNotificationActiveData.fulfilled, (state, action) => {
        const { id, isActive } = action.meta.arg
        const existing = state.notifications[id] || {}

        state.notifications[id] = {
          ...existing,
          isActive,
        }

        state.notificationsStatus = 'succeeded'
      })
      .addCase(getAllNotificationTemplatesData.fulfilled, (state, action) => {
        action.payload.forEach((template) => {
          const existing = state.notificationTemplates[template.id] || {}

          state.notificationTemplates[template.id] = {
            ...existing,
            ...template,
          }
        })

        state.notificationTemplatesStatus = 'succeeded'
      })
      .addCase(createNotificationTemplateData.fulfilled, (state, action) => {
        const data = action.meta.arg
        const id = action.payload
        const now = new Date().toISOString()
        const newTemplate = {
          id,
          ...data,
          createdAt: now,
          updatedAt: now,
        }

        state.notificationTemplates = {
          [id]: newTemplate,
          ...state.notificationTemplates,
        }

        state.notificationTemplatesStatus = 'succeeded'
      })
      .addCase(updateNotificationTemplateData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        const existing = state.notificationTemplates[id] || {}
        const now = new Date().toISOString()

        state.notificationTemplates[id] = {
          ...existing,
          ...data,
          updatedAt: now,
        }

        state.notificationTemplatesStatus = 'succeeded'
      })
      .addCase(deleteNotificationTemplateData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.notificationTemplates[id]

        state.notificationTemplatesStatus = 'succeeded'
      })
  },
})

export default notificationSlice.reducer
